package com.eladfinish.bible.contest.screens.browse.tableOfContents

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.data.common.FileStorage
import com.eladfinish.bible.contest.empty_mikud
import com.eladfinish.bible.contest.error
import com.eladfinish.bible.contest.extensions.runIfNot
import com.eladfinish.bible.contest.preset_completions
import com.eladfinish.bible.contest.saved_completions
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabsState.DialogState
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.BookmarkData
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.BrowseMode
import com.eladfinish.bible.contest.screens.browse.tableOfContents.ui.dialogs.DEFAULT_FONT_SIZE_SP
import com.eladfinish.bible.contest.screens.chapterComplete.model.BlanksRecord
import com.eladfinish.bible.contest.screens.chapterComplete.model.RemoteCompletionJsonKeys
import com.eladfinish.bible.contest.screens.chapterComplete.usecases.GetPresetBlanksUseCase
import com.eladfinish.bible.contest.tab_bible
import com.eladfinish.bible.contest.tab_chumash
import com.eladfinish.bible.contest.tab_misc
import com.eladfinish.bible.contest.tab_sedarim
import com.eladfinish.bible.contest.ui.menus.actions.BrowseToolbarAction
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

class BrowseTabsStore(
    params: BrowseTabsStoreParams,
    private val getPresetBlanksUseCase: GetPresetBlanksUseCase,
    private val fileStorage: FileStorage,
    private val settings: Settings,
) : ViewModelStore<BrowseTabsState, BrowseTabsAction, BrowseTabsEffect>(
    tag = "BrowseTabsStore",
) {

    /**
     * @property opensReference the screen is opened at a given chapter (a link, the random verse screen): it passes
     * straight on to the chapter, so the help of the table of contents is not shown over it.
     */
    data class BrowseTabsStoreParams(
        val isCompleteChapter: Boolean,
        val opensReference: Boolean = false,
    )

    private val isCompleteChapter = params.isCompleteChapter
    private val opensReference = params.opensReference

    companion object {
        private const val CHAPTER_COMPLETION_FOLDER = "chapter completion"
        const val SKIP_READ_ME_PREF = "skipReadMeBrowse"
    }

    private val isSkipReadMeChecked: Boolean
        get() = settings.getBoolean(key = SKIP_READ_ME_PREF, defaultValue = false)

    private val defaultState: BrowseTabsState
        get() = BrowseTabsState(
            tabs = getTabs(),
            isSkipReadMeChecked = isSkipReadMeChecked,
            isLoading = false,
            dialogState = if (isSkipReadMeChecked || opensReference) {
                DialogState.None
            } else {
                DialogState.Help(isDontShowAgainChecked = isSkipReadMeChecked)
            },
        )

    override val stateFlow = MutableStateFlow(defaultState)

    override fun handleAction(action: BrowseTabsAction): BrowseTabsState =
        when (action) {
            is BrowseTabsAction.ToggleHelp -> {
                handleToggleHelp(checked = action.shouldShowNextTime)
            }

            is BrowseTabsAction.ToolbarClicked -> handleToolbarClicked(action.toolbarAction)
            BrowseTabsAction.HideDialog -> handleHideDialog()
            is BrowseTabsAction.UpdateFontSize -> handleUpdateFontSize(action.value)
            is BrowseTabsAction.LoadChapterCompletionByIndex -> handleLoadChapterCompletionByIndex(action.index)
            is BrowseTabsAction.LoadRemoteChapterCompletion -> handleLoadRemoteChapterCompletion(action.blanksRecord)
            BrowseTabsAction.BackClicked -> handleBackClicked()
        }

    private fun handleBackClicked(): BrowseTabsState {
        return onlyEffect(BrowseTabsEffect.NavigateToBibleTab)
    }

    private fun handleToolbarClicked(toolbarAction: BrowseToolbarAction): BrowseTabsState {
        return when (toolbarAction) {
            BrowseToolbarAction.ChangeFontSize -> {
                handleChangeFontSizeClicked()
            }

            BrowseToolbarAction.Help -> {
                handleHelpClicked()
            }

            BrowseToolbarAction.LoadSavedCompletions -> {
                handleLoadSavedCompletions()
            }

            BrowseToolbarAction.OpenBookmark -> {
                handleOpenBookmark()
            }
        }
    }

    private fun handleHideDialog(): BrowseTabsState {
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleToggleHelp(checked: Boolean): BrowseTabsState {
        settings.putBoolean(key = SKIP_READ_ME_PREF, value = checked)
        return currentState.copy(isSkipReadMeChecked = checked)
    }

    private fun handleHelpClicked(): BrowseTabsState {
        return currentState.copy(dialogState = DialogState.Help(isDontShowAgainChecked = currentState.isSkipReadMeChecked))
    }

    private fun handleChangeFontSizeClicked(): BrowseTabsState {
        val currentFontSize = settings.getFloat(
            key = Constants.PREF_CHAPTER_CONTENT_FONT_SIZE,
            defaultValue = DEFAULT_FONT_SIZE_SP,
        )
        return currentState.copy(dialogState = DialogState.FontSize(currentFontSize = currentFontSize))
    }

    private fun handleUpdateFontSize(value: Float): BrowseTabsState {
        settings.putFloat(
            key = Constants.PREF_CHAPTER_CONTENT_FONT_SIZE,
            value = value,
        )
        return currentState
    }

    private fun handleLoadSavedCompletions(): BrowseTabsState {
        val presets = listOf(SharedRes.strings.preset_completions.desc())
        val savedFiles = fileStorage.listFiles(CHAPTER_COMPLETION_FOLDER)
        return currentState.copy(
            dialogState = DialogState.SavedCompletions(
                title = SharedRes.strings.saved_completions.desc(),
                presets = presets.toImmutableList(),
                savedItems = savedFiles.toImmutableList(),
            )
        )
    }

    private fun handleLoadChapterCompletionByIndex(index: Int): BrowseTabsState {
        if (index == 0) { // השלמות מוכנות
            launchEffect {
                updateState {
                    it.copy(isLoading = true)
                }
                val result = getPresetBlanksUseCase()
                updateState {
                    it.copy(
                        isLoading = false,
                        dialogState = DialogState.RemoteSavedCompletions(
                            title = SharedRes.strings.preset_completions.desc(),
                            items = result.first.toImmutableList(),
                            blanksRecords = result.second.toImmutableList(),
                        )
                    )
                }
            }
        } else {
            // TODO: handle saved completions
//            val savedCompletion = savedCompletionsList[index]
//            Napier.d("finalSavedCompletions which: $index")
//            try {
//                val filename = folder + File.separator + savedCompletion + ".txt"
//                val savedCompletionDataRaw = FilesUtils.readFileFromExternalStorage(requireContext(), filename)
//                Napier.d("savedCompletionDataRaw $savedCompletionDataRaw")
//                if (savedCompletionDataRaw.isEmpty()) {
//                    toast(SharedRes.strings.empty_mikud)
//                    return
//                }
//                val startOfJson = savedCompletionDataRaw.indexOf("{")
//                val endOfJson = savedCompletionDataRaw.indexOf("}")
//                Napier.d("startOfJson $startOfJson endOfJson $endOfJson")
//                val jsonStr = savedCompletionDataRaw.substring(startOfJson, endOfJson + 1)
//                val savedCompletionDataJson = jsonStr.convertToDataClass<SavedCompletionData>()
//                Napier.d("savedCompletionDataTest $savedCompletionDataJson")
//                val (bookEnglish, bookHebrew, bookIndex, chapter, blanks) = savedCompletionDataJson
//                val msg = "#$bookIndex $bookEnglish $bookHebrew $chapter: $blanks"
//                Napier.d("loadSavedCompletions msg: $msg")
//                showContent(bookIndex = bookIndex, chapterIndex = chapter, completionDetails = jsonStr)
//            } catch (e: Exception) {
//                AppCrashlytics.logException(e)
//                toast(getString(SharedRes.strings.error) + "\n" + e.message)
//            }
        }
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleLoadRemoteChapterCompletion(blanksRecord: BlanksRecord): BrowseTabsState {
        with(blanksRecord) {
            val indexesArray = convertObjectToArray(indexes)
            val completionDetails = buildJsonObject {
                put(key = RemoteCompletionJsonKeys.BOOK_INDEX, value = bookIndex)
                put(key = RemoteCompletionJsonKeys.CHAPTER_INDEX, value = chapterIndex)
                put(key = RemoteCompletionJsonKeys.BLANKS, element = indexesArray)
            }
            launchEffect {
                sideEffectFlow.emit(
                    BrowseTabsEffect.HandleRemoteChapterCompletionSelected(
                        bookIndex = bookIndex,
                        chapterIndex = chapterIndex,
                        completionDetails = completionDetails.toString(),
                    )
                )
            }
        }
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun convertObjectToArray(indexes: Map<String, List<Int>>): JsonArray =
        indexes.keys.sortedWith { s1, s2 -> s1.asNumber().compareTo(s2.asNumber()) }
            .map {
                JsonArray(indexes[it]!!.map { num -> JsonPrimitive(num) })
            }.let {
                JsonArray(it)
            }

    private fun String.asNumber(): Int {
        return removePrefix("v").toInt()
    }

    private fun handleOpenBookmark(): BrowseTabsState {
        val bookmarkBrowseMode = settings.getString(key = Constants.PREF_BOOKMARK_BROWSE_MODE, defaultValue = "")
        return if (bookmarkBrowseMode.isEmpty()) {
            launchEffect {
                sideEffectFlow.emit(BrowseTabsEffect.NotifyNoBookmark)
            }
            currentState
        } else {
            val bookmarkBook = settings.getInt(key = Constants.PREF_BOOKMARK_BOOK, defaultValue = 0)
            val bookmarkChapter = settings.getInt(key = Constants.PREF_BOOKMARK_CHAPTER, defaultValue = 0)
            val bookmarkScrollY = settings.getInt(key = Constants.PREF_BOOKMARK_SCROLL_Y, defaultValue = 0)
            val bookmarkTextOffset = settings.getInt(key = Constants.PREF_BOOKMARK_TEXT_OFFSET, defaultValue = -1)
            // TODO: 21/04/2017 fix bug of bookmark_onqelos:
            val bookmarkOnqelos = settings.getBoolean(key = Constants.PREF_BOOKMARK_ONQELOS, defaultValue = false)
            Napier.d("browseMode $bookmarkBrowseMode")
            Napier.d("bookmark_book $bookmarkBook")
            Napier.d("bookmark_chapter $bookmarkChapter")
            Napier.d("bookmark_scrollY $bookmarkScrollY")
            Napier.d("bookmark_bookmarkOnqelos $bookmarkOnqelos")
            when (bookmarkBrowseMode) {
                BrowseMode.BIBLE_MODE.name -> {
                    BookmarkData(
                        browseMode = BrowseMode.BIBLE_MODE,
                        bookIndex = bookmarkBook,
                        partIndex = bookmarkChapter,
                        bookmarkScrollY = bookmarkScrollY,
                        bookmarkTextOffset = bookmarkTextOffset,
                        showOnqelos = bookmarkOnqelos,
                    )
                }

                BrowseMode.CHUMASH_MODE.name -> {
                    BookmarkData(
                        browseMode = BrowseMode.CHUMASH_MODE,
                        bookIndex = bookmarkBook,
                        partIndex = bookmarkChapter,
                        bookmarkScrollY = bookmarkScrollY,
                        bookmarkTextOffset = bookmarkTextOffset,
                        showOnqelos = bookmarkOnqelos,
                    )
                }

                else -> null
            }?.let {
                launchEffect {
                    sideEffectFlow.emit(BrowseTabsEffect.HandleBookmark(bookmarkData = it))
                }
            }
            currentState
        }
    }

    private fun getTabs(): ImmutableList<StringDesc> = mutableListOf(
        SharedRes.strings.tab_bible,
    ).runIfNot(condition = isCompleteChapter) {
        apply {
            addAll(
                listOf(
                    SharedRes.strings.tab_chumash,
                    SharedRes.strings.tab_sedarim,
                    SharedRes.strings.tab_misc,
                )
            )
        }
    }.map { it.desc() }
        .toImmutableList()

}
